/**
 * LifetimeCommissionPage — Trackier-style CRUD list for lifetime-commission.
 *
 * Layout:
 *   - topbar: "Lifetime Commission" title + Create Rule button
 *   - filter bar: publisher + customer + lookback-days (client-side filter over list())
 *   - table: publisher | customer-id | first-conv | ltv | commissions-paid | status
 *   - create drawer (right slide-over): RHF + zod
 *       name (label only, kept client-side), lookback-days (informational),
 *       rate% (-> commission_pct_bps), cap (-> lifetime_value_cents ceiling)
 *       + customer_id / affiliate_id (BE required)
 *
 * Design tokens per task: GREEN accent only (green-600/700/50), white
 * surfaces, zinc-200 borders, zinc-900 text, 6-8px rounded. Inter via
 * tailwind defaults. Inline SVG icons only. No emoji / dark mode /
 * competitor libs (shadcn/lucide/heroui).
 */
import { useMemo, useState, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
	useLifetimeCommissionList,
	useLifetimeCommissionCreate,
} from "./hooks";
import type {
	CreateLifetimeCommissionBody,
	LifetimeCommissionRow,
} from "./api";

// ---- style tokens ---------------------------------------------------------

const CARD = "rounded-lg border border-zinc-200 bg-white shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:cursor-not-allowed disabled:bg-zinc-50";
const LABEL = "flex flex-col gap-1 text-xs font-medium text-zinc-700";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-md border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"inline-flex items-center gap-1.5 rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---- icons ----------------------------------------------------------------

function PlusIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			fill="none"
			className="h-4 w-4"
		>
			<path
				d="M10 4v12M4 10h12"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
			/>
		</svg>
	);
}

function XIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			fill="none"
			className="h-4 w-4"
		>
			<path
				d="M5 5l10 10M15 5L5 15"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
			/>
		</svg>
	);
}

function SearchIcon() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			fill="none"
			className="h-4 w-4 text-zinc-400"
		>
			<circle cx="9" cy="9" r="5" stroke="currentColor" strokeWidth="1.75" />
			<path
				d="M13 13l3 3"
				stroke="currentColor"
				strokeWidth="1.75"
				strokeLinecap="round"
			/>
		</svg>
	);
}

// ---- zod schema for create form ------------------------------------------

const createSchema = z.object({
	name: z
		.string()
		.trim()
		.min(1, "Name is required")
		.max(80, "Max 80 chars"),
	customer_id: z.string().trim().min(1, "Customer ID is required"),
	affiliate_id: z.string().trim().min(1, "Publisher ID is required"),
	campaign_id: z.string().trim().optional(),
	lookback_days: z
		.number({ message: "Must be a number" })
		.int()
		.min(1, "Min 1 day")
		.max(3650, "Max 3650 days"),
	rate_pct: z
		.number({ message: "Must be a number" })
		.min(0, "Min 0%")
		.max(100, "Max 100%"),
	cap_cents: z
		.number({ message: "Must be a number" })
		.min(0, "Min 0"),
});

type CreateFormValues = z.infer<typeof createSchema>;

const defaultCreateValues: CreateFormValues = {
	name: "",
	customer_id: "",
	affiliate_id: "",
	campaign_id: "",
	lookback_days: 365,
	rate_pct: 10,
	cap_cents: 0,
};

// ---- helpers --------------------------------------------------------------

function formatUsd(cents: number): string {
	const dollars = cents / 100;
	return dollars.toLocaleString(undefined, {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: 2,
	});
}

function formatDate(unixSeconds: number): string {
	if (!unixSeconds) return "-";
	const d = new Date(unixSeconds * 1000);
	if (Number.isNaN(d.valueOf())) return "-";
	return d.toISOString().slice(0, 10);
}

function rowStatus(row: LifetimeCommissionRow, lookbackDays: number): {
	label: string;
	tone: "active" | "expired" | "pending";
} {
	if (!row.first_referral_at) {
		return { label: "Pending", tone: "pending" };
	}
	const now = Math.floor(Date.now() / 1000);
	const ageDays = (now - row.first_referral_at) / 86400;
	if (lookbackDays > 0 && ageDays > lookbackDays) {
		return { label: "Expired", tone: "expired" };
	}
	return { label: "Active", tone: "active" };
}

function toneClasses(tone: "active" | "expired" | "pending"): string {
	if (tone === "active") {
		return "bg-green-50 text-green-700 ring-green-600/20";
	}
	if (tone === "expired") {
		return "bg-zinc-100 text-zinc-600 ring-zinc-300";
	}
	return "bg-amber-50 text-amber-700 ring-amber-600/20";
}

// ---- component ------------------------------------------------------------

export function LifetimeCommissionPage() {
	const listQuery = useLifetimeCommissionList();
	const createMut = useLifetimeCommissionCreate();

	const [publisherFilter, setPublisherFilter] = useState("");
	const [customerFilter, setCustomerFilter] = useState("");
	const [lookbackDays, setLookbackDays] = useState(365);
	const [drawerOpen, setDrawerOpen] = useState(false);

	const form = useForm<CreateFormValues>({
		resolver: zodResolver(createSchema),
		defaultValues: defaultCreateValues,
		mode: "onSubmit",
	});

	const items = listQuery.data?.items ?? [];

	const filtered = useMemo(() => {
		const pub = publisherFilter.trim().toLowerCase();
		const cust = customerFilter.trim().toLowerCase();
		return items.filter((row) => {
			if (pub && !row.affiliate_id.toLowerCase().includes(pub)) return false;
			if (cust && !row.customer_id.toLowerCase().includes(cust)) return false;
			return true;
		});
	}, [items, publisherFilter, customerFilter]);

	const totals = useMemo(() => {
		let ltv = 0;
		let paid = 0;
		for (const r of filtered) {
			ltv += r.lifetime_value_cents;
			paid += r.commission_earned_cents;
		}
		return { ltv, paid };
	}, [filtered]);

	const handleOpenDrawer = () => {
		form.reset(defaultCreateValues);
		createMut.reset();
		setDrawerOpen(true);
	};

	const handleCloseDrawer = () => {
		if (createMut.isPending) return;
		setDrawerOpen(false);
	};

	const handleResetFilters = () => {
		setPublisherFilter("");
		setCustomerFilter("");
		setLookbackDays(365);
	};

	const onSubmit = (values: CreateFormValues) => {
		const body: CreateLifetimeCommissionBody = {
			customer_id: values.customer_id.trim(),
			affiliate_id: values.affiliate_id.trim(),
			commission_pct_bps: Math.round(values.rate_pct * 100),
			lifetime_value_cents: Math.max(0, Math.floor(values.cap_cents)),
			campaign_id: values.campaign_id?.trim() ? values.campaign_id.trim() : null,
		};
		createMut.mutate(body, {
			onSuccess: () => {
				setDrawerOpen(false);
				form.reset(defaultCreateValues);
			},
		});
	};

	const stopFormClose = (e: FormEvent) => {
		e.stopPropagation();
	};

	return (
		<main
			data-testid="lifetime-commission-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-10"
		>
			{/* Topbar */}
			<header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-1.5">
					<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
						Commissions
					</p>
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Lifetime Commission
					</h1>
					<p className="max-w-2xl text-sm text-zinc-500">
						Recurring commissions paid to publishers over a customer&apos;s
						lifetime. Configure rate, cap, and lookback window per rule.
					</p>
				</div>
				<div className="flex items-center gap-3">
					<span
						className="text-xs text-zinc-500"
						data-testid="lifetime-commission-count"
					>
						{filtered.length} customer{filtered.length === 1 ? "" : "s"}
					</span>
					<button
						type="button"
						onClick={handleOpenDrawer}
						className={BTN_PRIMARY}
						data-testid="lifetime-commission-new"
					>
						<PlusIcon />
						Create Rule
					</button>
				</div>
			</header>

			{/* Filter bar */}
			<section
				className={CARD + " p-4"}
				aria-label="Filters"
				data-testid="lifetime-commission-filters"
			>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-end">
					<label className={LABEL + " flex-1"}>
						Publisher
						<div className="relative">
							<span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
								<SearchIcon />
							</span>
							<input
								type="text"
								className={INPUT + " pl-8"}
								placeholder="pub_abc..."
								value={publisherFilter}
								onChange={(e) => setPublisherFilter(e.currentTarget.value)}
								data-testid="lifetime-commission-filter-publisher"
							/>
						</div>
					</label>
					<label className={LABEL + " flex-1"}>
						Customer
						<div className="relative">
							<span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
								<SearchIcon />
							</span>
							<input
								type="text"
								className={INPUT + " pl-8"}
								placeholder="cust_123..."
								value={customerFilter}
								onChange={(e) => setCustomerFilter(e.currentTarget.value)}
								data-testid="lifetime-commission-filter-customer"
							/>
						</div>
					</label>
					<label className={LABEL + " w-full sm:w-48"}>
						Lookback (days)
						<input
							type="number"
							min={1}
							max={3650}
							step={1}
							className={INPUT}
							value={lookbackDays}
							onChange={(e) =>
								setLookbackDays(Math.max(1, Number(e.currentTarget.value) || 1))
							}
							data-testid="lifetime-commission-filter-lookback"
						/>
					</label>
					<button
						type="button"
						onClick={handleResetFilters}
						className={BTN_SECONDARY}
						data-testid="lifetime-commission-filter-reset"
					>
						Reset
					</button>
				</div>
			</section>

			{/* Summary strip */}
			<section
				className={CARD + " p-4"}
				aria-label="Summary"
				data-testid="lifetime-commission-summary"
			>
				<dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					<div>
						<dt className="text-xs font-medium text-zinc-500">Customers</dt>
						<dd className="mt-1 text-2xl font-semibold text-zinc-900">
							{filtered.length}
						</dd>
					</div>
					<div>
						<dt className="text-xs font-medium text-zinc-500">Total LTV</dt>
						<dd className="mt-1 text-2xl font-semibold text-zinc-900">
							{formatUsd(totals.ltv)}
						</dd>
					</div>
					<div>
						<dt className="text-xs font-medium text-zinc-500">
							Commissions paid
						</dt>
						<dd className="mt-1 text-2xl font-semibold text-green-700">
							{formatUsd(totals.paid)}
						</dd>
					</div>
				</dl>
			</section>

			{/* Table */}
			<section className={CARD} aria-label="Lifetime commission list">
				<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
					<h2 className="text-sm font-semibold text-zinc-900">Commissions</h2>
				</div>
				{listQuery.isLoading ? (
					<p
						className="px-4 py-6 text-sm text-zinc-500"
						data-testid="lifetime-commission-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p
						className="px-4 py-6 text-sm text-red-600"
						role="alert"
						data-testid="lifetime-commission-error"
					>
						{listQuery.error.message}
					</p>
				) : filtered.length === 0 ? (
					<div
						className="flex flex-col items-center gap-2 px-4 py-10 text-center"
						data-testid="lifetime-commission-empty"
					>
						<p className="text-sm font-medium text-zinc-900">
							No customers yet
						</p>
						<p className="text-xs text-zinc-500">
							Adjust filters or create a new rule to start tracking lifetime
							commissions.
						</p>
						<button
							type="button"
							onClick={handleOpenDrawer}
							className={BTN_PRIMARY + " mt-2"}
							data-testid="lifetime-commission-empty-new"
						>
							<PlusIcon />
							Create Rule
						</button>
					</div>
				) : (
					<div className="overflow-x-auto">
						<table
							className="min-w-full divide-y divide-zinc-200 text-sm"
							data-testid="lifetime-commission-table"
						>
							<thead className="bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
								<tr>
									<th scope="col" className="px-4 py-2.5">
										Publisher
									</th>
									<th scope="col" className="px-4 py-2.5">
										Customer ID
									</th>
									<th scope="col" className="px-4 py-2.5">
										First conv.
									</th>
									<th scope="col" className="px-4 py-2.5 text-right">
										LTV
									</th>
									<th scope="col" className="px-4 py-2.5 text-right">
										Commissions paid
									</th>
									<th scope="col" className="px-4 py-2.5">
										Status
									</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-200 bg-white">
								{filtered.map((row) => {
									const st = rowStatus(row, lookbackDays);
									return (
										<tr
											key={row.id}
											className="hover:bg-zinc-50"
											data-testid={`lifetime-commission-row-${row.id}`}
										>
											<td className="px-4 py-3 font-mono text-xs text-zinc-700">
												{row.affiliate_id}
											</td>
											<td className="px-4 py-3 font-mono text-xs text-zinc-700">
												{row.customer_id}
											</td>
											<td className="px-4 py-3 text-zinc-700">
												{formatDate(row.first_referral_at)}
											</td>
											<td className="px-4 py-3 text-right text-zinc-900">
												{formatUsd(row.lifetime_value_cents)}
											</td>
											<td className="px-4 py-3 text-right font-medium text-green-700">
												{formatUsd(row.commission_earned_cents)}
											</td>
											<td className="px-4 py-3">
												<span
													className={
														"inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset " +
														toneClasses(st.tone)
													}
												>
													{st.label}
												</span>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</section>

			{/* Drawer */}
			{drawerOpen ? (
				<div
					className="fixed inset-0 z-40 flex"
					data-testid="lifetime-commission-drawer"
					role="dialog"
					aria-modal="true"
					aria-label="Create lifetime commission rule"
				>
					<button
						type="button"
						className="absolute inset-0 bg-zinc-900/40"
						onClick={handleCloseDrawer}
						aria-label="Close drawer"
						data-testid="lifetime-commission-drawer-backdrop"
					/>
					<aside className="relative ml-auto flex h-full w-full max-w-md flex-col bg-white shadow-xl">
						<div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
							<div>
								<h2 className="text-base font-semibold text-zinc-900">
									Create Rule
								</h2>
								<p className="text-xs text-zinc-500">
									Define a lifetime commission rule for a publisher.
								</p>
							</div>
							<button
								type="button"
								onClick={handleCloseDrawer}
								className="rounded-md p-1 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
								aria-label="Close"
								data-testid="lifetime-commission-drawer-close"
							>
								<XIcon />
							</button>
						</div>
						<form
							onClick={stopFormClose}
							onSubmit={form.handleSubmit(onSubmit)}
							className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-5"
							data-testid="lifetime-commission-create-form"
							noValidate
						>
							<label className={LABEL}>
								Name
								<input
									type="text"
									className={INPUT}
									placeholder="e.g. Premium publishers / annual"
									{...form.register("name")}
									data-testid="lifetime-commission-field-name"
								/>
								{form.formState.errors.name ? (
									<span className="text-xs text-red-600" role="alert">
										{form.formState.errors.name.message}
									</span>
								) : null}
							</label>

							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
								<label className={LABEL}>
									Publisher ID
									<input
										type="text"
										className={INPUT}
										placeholder="pub_abc"
										{...form.register("affiliate_id")}
										data-testid="lifetime-commission-field-affiliate"
									/>
									{form.formState.errors.affiliate_id ? (
										<span className="text-xs text-red-600" role="alert">
											{form.formState.errors.affiliate_id.message}
										</span>
									) : null}
								</label>
								<label className={LABEL}>
									Customer ID
									<input
										type="text"
										className={INPUT}
										placeholder="cust_123"
										{...form.register("customer_id")}
										data-testid="lifetime-commission-field-customer"
									/>
									{form.formState.errors.customer_id ? (
										<span className="text-xs text-red-600" role="alert">
											{form.formState.errors.customer_id.message}
										</span>
									) : null}
								</label>
							</div>

							<label className={LABEL}>
								Campaign ID <span className="text-zinc-400">(optional)</span>
								<input
									type="text"
									className={INPUT}
									placeholder="cmp_xyz"
									{...form.register("campaign_id")}
									data-testid="lifetime-commission-field-campaign"
								/>
							</label>

							<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
								<label className={LABEL}>
									Lookback (days)
									<input
										type="number"
										min={1}
										max={3650}
										step={1}
										className={INPUT}
										{...form.register("lookback_days", { valueAsNumber: true })}
										data-testid="lifetime-commission-field-lookback"
									/>
									{form.formState.errors.lookback_days ? (
										<span className="text-xs text-red-600" role="alert">
											{form.formState.errors.lookback_days.message}
										</span>
									) : null}
								</label>
								<label className={LABEL}>
									Rate (%)
									<input
										type="number"
										min={0}
										max={100}
										step={0.1}
										className={INPUT}
										{...form.register("rate_pct", { valueAsNumber: true })}
										data-testid="lifetime-commission-field-rate"
									/>
									{form.formState.errors.rate_pct ? (
										<span className="text-xs text-red-600" role="alert">
											{form.formState.errors.rate_pct.message}
										</span>
									) : null}
								</label>
								<label className={LABEL}>
									Cap (cents)
									<input
										type="number"
										min={0}
										step={1}
										className={INPUT}
										{...form.register("cap_cents", { valueAsNumber: true })}
										data-testid="lifetime-commission-field-cap"
									/>
									{form.formState.errors.cap_cents ? (
										<span className="text-xs text-red-600" role="alert">
											{form.formState.errors.cap_cents.message}
										</span>
									) : null}
								</label>
							</div>

							{createMut.error ? (
								<p
									className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
									role="alert"
									data-testid="lifetime-commission-create-error"
								>
									{createMut.error.message}
								</p>
							) : null}

							<div className="mt-auto flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
								<button
									type="button"
									onClick={handleCloseDrawer}
									className={BTN_SECONDARY}
									disabled={createMut.isPending}
									data-testid="lifetime-commission-drawer-cancel"
								>
									Cancel
								</button>
								<button
									type="submit"
									className={BTN_PRIMARY}
									disabled={createMut.isPending}
									data-testid="lifetime-commission-submit"
								>
									{createMut.isPending ? "Saving." : "Create Rule"}
								</button>
							</div>
						</form>
					</aside>
				</div>
			) : null}
		</main>
	);
}

export default LifetimeCommissionPage;
